import * as React from 'react';

import { DeliveryPersonDTO, Permission, Res } from 'core/api';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { Content, Filler, Header, PosScreen, ActionButton, Footer } from 'pos/ui';
import * as Images from 'pos/Images';
import DeliveryPersonStatusList from 'pos/ui/DeliveryPersonList';
import { RestaurantFacade } from 'pos/facades';
import addDeliveryPersonBalanceDelta, { DeliveryPersonBalanceMovementType } from './addDeliveryPersonBalanceDelta';
import { Message, Modal } from 'core/ui-next/modal';
import { HStack, VStack, Box, Text } from '@chakra-ui/react';
import { SvgIcon } from 'core/ui-next';

type Props = {
	deliveryPersons: DeliveryPersonDTO[];
} & Modal.Actions<void>;

type State = {
	deliveryPersons: DeliveryPersonDTO[];
	selectedDeliveryPerson: DeliveryPersonDTO;
};

function ShowDeliveryPersonStatus(props: Props) {

	const { deliveryPersons, cancel } = props;

	const [state, setState] = React.useState<State>({
		deliveryPersons,
		selectedDeliveryPerson: deliveryPersons[0]
	});



	const onUpdatePersons = React.useCallback(async () => {
		const deliveryPersons = await RestaurantFacade.getDeliveryPersons();
		const selectedDeliveryPerson = deliveryPersons.find(dp => dp.UserId === state.selectedDeliveryPerson.UserId) || deliveryPersons[0];
		setState({ deliveryPersons, selectedDeliveryPerson });
	}, [state.selectedDeliveryPerson]);

	const onAddBalance = React.useCallback(async () => {
		const result = await addDeliveryPersonBalanceDelta(state.selectedDeliveryPerson, DeliveryPersonBalanceMovementType.Add);
		if (result.ok) {
			await onUpdatePersons();
		}
	}, [state.selectedDeliveryPerson, onUpdatePersons]);

	const onSubstractBalance = React.useCallback(async () => {
		const result = await addDeliveryPersonBalanceDelta(state.selectedDeliveryPerson, DeliveryPersonBalanceMovementType.Substract);
		if (result.ok) {
			await onUpdatePersons();
		}
	}, [state.selectedDeliveryPerson, onUpdatePersons]);



	const getTotalDelivering = (): number => {
		return state.deliveryPersons.reduce((sum, dp) => sum + dp.DeliveringOrders, 0);
	};

	const getTotalDelivered = (): number => {
		return state.deliveryPersons.reduce((sum, dp) => sum + dp.DeliveredOrders, 0);
	};



	return <PosScreen size={'medium'} screenId={'SHOW_DELIVERY_PERSON_STATUS'} useKeyPad={false}>
		<Header title={Res.Web.SHOW_DELIVERY_PERSONS_BALANCE()} image={Images.DeliveryPersonBalance} onClose={cancel} />
		<Content>
			<HStack gap={'6'} justifyContent={'space-around'} paddingX={'6'} paddingY={'3'} borderBottom={'1px solid'} borderColor={'neutral.300'} >
				<Box gap={'1.5'} width={'55.2'} height={'8'} bg={{ base: 'yellow.200', _dark: 'yellow.300' }} borderRadius={'md'} justifyContent={'center'} alignItems={'center'} display={'flex'} color={'neutral.800'}>
					<SvgIcon src={Images.DeliveryMotorcycle} width={'4.5'} height={'4.5'} />
					<Text fontWeight={'semibold'} fontSize={'sm'}>{Res.Web.SHOW_DELIVERY_PERSONS_ON_DELIVERY_LBL() + '  - '}</Text>
					<Text fontWeight={'bold'} fontSize={'md'}>{getTotalDelivering()}</Text>
				</Box>
				<Box gap={'1.5'} width={'55.2'} height={'8'} bg={{ base: 'green.200', _dark: 'green.300' }} borderRadius={'md'} justifyContent={'center'} alignItems={'center'} display={'flex'} color={'neutral.800'}>
					<SvgIcon src={Images.Delivered} width={'4.5'} height={'4.5'} />
					<Text fontWeight={'semibold'} fontSize={'sm'}>{Res.Web.SHOW_DELIVERY_PERSONS_DELIVERED_LBL() + '  - '}</Text>
					<Text fontWeight={'bold'} fontSize={'md'}>{getTotalDelivered()}</Text>
				</Box>
			</HStack>
			<VStack gap={'6'} paddingY={'6'} paddingX={'24'} width={'full'} justifyContent={'center'}>
				<Text fontWeight={'semibold'} fontSize={'md'} color={{ base: 'neutral.800', _dark: 'white' }}>{Res.Web.SHOW_DELIVERY_PERSONS_ACTIVIITY_BY_DELIVERY_PERSON_LBL()}</Text>
				<VStack maxH={'87.5'} w={'full'}>
					<DeliveryPersonStatusList deliveryPersons={state.deliveryPersons} selected={state.selectedDeliveryPerson} onDeliveryPersonSelected={(deliveryPerson) => setState({ ...state, selectedDeliveryPerson: deliveryPerson })} />
				</VStack>
			</VStack>
		</Content>
		<Footer>
			<Filler />
			<ActionButton image={Images.RemovePayment} title={Res.Web.SHOW_DELIVERY_PERSONS_SUBSTRACT_BALANCE_BTN()} action={() => onSubstractBalance()} />
			<ActionButton image={Images.AddPayment} title={Res.Web.SHOW_DELIVERY_PERSONS_ADD_BALANCE_BTN()} action={() => onAddBalance()} />
		</Footer>
	</PosScreen>;
}


declare global {
	interface CommandType {
		showDeliveryPersonStatuses: () => Promise<Result>
	}
}

Command.register('showDeliveryPersonStatuses', Preconditions.ensure(Permission.DeliveryPerson_ShowStatus), async (): Promise<Result> => {
	const deliveryPersons = await RestaurantFacade.getDeliveryPersons();
	if (deliveryPersons.length === 0) {
		await Message.show(Res.Msg.MSG_NOT_EXISTS_DELIVERY_PERSONS());
		return Result.error();
	}

	await Modal.show(ShowDeliveryPersonStatus, { deliveryPersons: deliveryPersons });
	return Result.ok();
});